/**
 * Validator Utilities
 *
 * Aligned with BT Pay Checkout UI validation standards.
 * Source: BT Pay Checkout UI form_validation.md
 */

/**
 * Sanitize cardholder name input (BT Pay Checkout UI standard)
 * - Allows letters (a-z, A-Z), spaces, hyphens, and apostrophes
 * - Removes leading symbols
 * - Removes duplicate spaces/symbols
 *
 * @param value - Raw input value
 * @returns Sanitized value (max 80 characters)
 *
 * @example
 * sanitizeCardholderName("John123  Doe!!") // "John Doe"
 * sanitizeCardholderName("María  José")    // "María José"
 * sanitizeCardholderName("Jean-Paul")      // "Jean-Paul"
 * sanitizeCardholderName("O'Brien")        // "O'Brien"
 */
export const sanitizeCardholderName = (value: string): string => {
  return value
    .replace(/[^A-Za-z\s'-]/g, '') // Only letters, spaces, hyphens, apostrophes
    .replace(/^[\s'-]+/, '') // Remove leading symbols
    .replace(/[\s'-]{2,}/g, ' ') // No duplicate spaces/symbols
    .slice(0, 80); // Max 80 characters
};

/**
 * Validate cardholder name (BT Pay Checkout UI standard)
 * @param name - Cardholder name
 * @returns true if valid (2-80 characters)
 */
export const isValidCardholderName = (name: string): boolean => {
  const trimmed = name.trim();
  return trimmed.length >= 2 && trimmed.length <= 80;
};

/**
 * Validate expiry date format and value
 * Aligned with BT Pay Checkout UI implementation
 *
 * @param expiry - Expiry date in MM/YY format
 * @returns true if valid and not expired
 */
export const isValidExpiryDate = (expiry: string): boolean => {
  if (!expiry || !expiry.includes('/')) return false;

  const [month, year] = expiry.split('/');
  if (!month || !year) return false;

  // Validate month range
  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12) return false;

  // Year must be 2 digits
  if (year.length !== 2) return false;

  // Check if date is in the future (BT Pay standard)
  const yearNum = parseInt(`20${year}`, 10);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (yearNum < currentYear) {
    return false;
  }

  if (yearNum === currentYear && monthNum < currentMonth) {
    return false;
  }

  return true;
};

/**
 * Format expiry date with automatic slash insertion
 * Aligned with BT Pay Checkout UI UX pattern
 *
 * @param value - Raw expiry input (e.g., "1225")
 * @returns Formatted expiry (e.g., "12/25")
 */
export const formatExpiryDate = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');

  // Only add slash once user starts typing the year (3+ digits)
  // Avoids backspace getting stuck on the slash with exactly 2 digits
  if (cleaned.length > 2) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
  }

  return cleaned;
};

export const getExpiryError = (expiry: string): string | null => {
  if (!expiry || expiry.length < 5) return null;
  const [month, year] = expiry.split('/');
  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12 || month?.length !== 2)
    return 'Enter a valid month';
  const yearNum = parseInt(`20${year}`, 10);
  const now = new Date();
  if (
    yearNum < now.getFullYear() ||
    (yearNum === now.getFullYear() && monthNum < now.getMonth() + 1)
  ) {
    return 'Card date expired';
  }
  return null;
};

/**
 * Format card number with spacing (BT Pay Checkout UI standard)
 *
 * @param cardNumber - Raw card number
 * @param cardType - Optional card type ('AMEX' for 4-6-5 format)
 * @returns Formatted card number
 * - Amex: 4-6-5 format (e.g., "3782 822463 10005")
 * - Others: 4-4-4-4 format (e.g., "4111 1111 1111 1111")
 */
export const formatCardNumber = (
  cardNumber: string,
  cardType?: string
): string => {
  const cleaned = cardNumber.replace(/\s/g, '');

  // American Express uses 4-6-5 format
  if (
    cardType === 'AMEX' ||
    cleaned.startsWith('34') ||
    cleaned.startsWith('37')
  ) {
    // BT Pay regex: /(\d{4})(\d{6})(\d{5})/
    return cleaned.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
  }

  // Default: 4-4-4-4 format (BT Pay regex with lookahead)
  return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
};

/**
 * Validate CVV code (BT Pay Checkout UI standard)
 *
 * @param cvv - CVV code
 * @param cardType - Card type ('AMEX' for 4-digit CVV)
 * @returns true if valid length
 */
export const isValidCVV = (cvv: string, cardType?: string): boolean => {
  const cvvLength = cvv.replace(/\D/g, '').length;

  if (cardType === 'AMEX') {
    // Amex uses 4-digit CVV (on front of card)
    return cvvLength === 4;
  }

  // Most cards use 3-digit CVV (on back of card)
  return cvvLength === 3;
};
